import axios from 'axios';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import API from '../config/api';
import { canCaptureDoctor } from './config';
import { cacheOwner } from './profileCache';
import { localStore } from './localStore';
import { assertViewerCanWrite } from '../utils/viewerOnly';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';

let selected;
let running = false;
const submissions = new Map();
const emit = () => window.dispatchEvent(new Event('offlineProfileChanged'));
const allowed = [
  ['PUT', /^\/api\/patients\/(?:[a-f\d]{24}|local_[0-9a-f-]+)$/i],
  ['PUT', /^\/api\/patients\/[a-f\d]{24}\/visits\/[^/]+\/receipts\/[^/]+$/i],
  ['POST', /^\/api\/patients\/[^/]+\/vitals$/],
  ['POST', /^\/api\/patients\/complaint-history$/],
  ['PUT', /^\/api\/patients\/[^/]+\/doctor\/[^/]+\/visits\/[^/]+$/],
  ['POST', /^\/api\/medical-history\/patient$/],
  ['POST', /^\/api\/(medical-reports|referrals)\/patient\/(?:[a-f\d]{24}|local_[0-9a-f-]+)$/i],
  ['PATCH', /^\/api\/(medical-reports|referrals)\/patient\/[a-f\d]{24}\/[^/]+$/i],
  ['POST', /^\/api\/patients\/reports\/upload-urls$/],
  ['POST', /^\/api\/(billing|patient-packages|external-services\/requests)$/],
  ['POST', /^\/api\/(assessment-forms\/responses|insurance\/programs\/apply)$/],
];
export const isProfileQueued = error => error?.code === 'OFFLINE_PROFILE_QUEUED';
export function selectOfflineProfile(patient) { selected = patient ? { ...patient, owner: cacheOwner() } : null; }
const revisionKey = (owner, id) => JSON.stringify([owner, id]);
export async function refreshProfileRevision(patient) {
  const owner = cacheOwner();
  if (!owner || !navigator.onLine || !auth.currentUser || !patient?._id) return;
  const token = await auth.currentUser.getIdToken();
  const result = await axios.get(`${API.BASE_URL}/api/offline/profile-state/${patient._id}`, {
    params: { doctorId: localStorage.getItem('doctorId') }, headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
  });
  if (cacheOwner() === owner) {
    await localStore.put('revisions', { key: revisionKey(owner, patient._id), revision: result.data.revision, patient: result.data.patient });
    return result.data.patient;
  }
}
export async function pendingProfileActions() {
  const owner = cacheOwner();
  if (!owner) return [];
  const rows = await localStore.list('profileActions');
  return cacheOwner() === owner ? rows.filter(row => row.owner === owner) : [];
}
function tracking(action, status, code = '') {
  // Firestore tracks lifecycle; the local journal is retained until an API receipt.
  // No tokens, arbitrary URLs or attachment bytes are stored in tracking documents.
  void setDoc(doc(db, 'dashboardProfileActions', action.actorUid, 'actions', action.operationId), {
    operationId: action.operationId, actorUid: action.actorUid, doctorId: action.request.doctorId,
    patientMongoId: action.request.patientMongoId, status, code, createdAt: action.createdAt,
  }).catch(() => { window.dispatchEvent(new CustomEvent('offlineReadStatus', { detail: { trackingError: true } })); });
}
function cairoDateKey(value) {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(value));
  const part = type => parts.find(item => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
function isRecoverablePrescription(action) {
  const body = action?.request?.body || {};
  return action?.status === 'attention' && action.error === 'PROFILE_CHANGED' &&
    action.request.method === 'PUT' && /^\/api\/patients\/[a-f\d]{24}$/i.test(action.request.path) &&
    typeof body.prescriptionTemplate === 'string';
}
async function recoverPrescriptionConflict(action, owner) {
  if (!isRecoverablePrescription(action) || !auth.currentUser) return false;
  try {
    const token = await auth.currentUser.getIdToken();
    const result = await axios.get(`${API.BASE_URL}/api/offline/profile-state/${action.request.patientMongoId}`, {
      params: { doctorId: action.request.doctorId }, headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
    });
    if (cacheOwner() !== owner) return false;
    const visits = Array.isArray(result.data?.patient?.visits) ? result.data.patient.visits : [];
    let visitId = action.request.body.visit_id;
    if (visitId) {
      const exists = visits.some(visit => String(visit.visit_id || visit._id) === String(visitId));
      if (!exists) return false;
    } else {
      // Old queued prescriptions did not contain visit_id. Repair only when
      // the Cairo action date identifies one visit, never when ambiguous.
      const actionDate = cairoDateKey(action.createdAt);
      const matches = visits.filter(visit => cairoDateKey(visit.date) === actionDate);
      if (matches.length !== 1) return false;
      visitId = matches[0].visit_id || matches[0]._id;
    }
    if (!visitId || !result.data?.revision) return false;
    action.request.body.visit_id = String(visitId);
    action.request.baseRevision = result.data.revision;
    action.status = 'pending';
    action.error = '';
    action.nextAttemptAt = 0;
    action.frozen = false;
    await localStore.put('profileActions', action);
    return true;
  } catch (_) { return false; }
}
export async function synchronizeProfileActions(hasBrowserLock = false) {
  if (!hasBrowserLock && navigator.locks && cacheOwner()) {
    return navigator.locks.request(`drwaiting-profile-sync:${cacheOwner()}`, { ifAvailable: true },
      lock => lock ? synchronizeProfileActions(true) : undefined);
  }
  if (running || !navigator.onLine || !cacheOwner()) return;
  running = true;
  try {
    const owner = cacheOwner();
    const blocked = new Set();
    for (const listed of (await pendingProfileActions()).sort((a, b) => a.createdAt - b.createdAt)) {
      const action = await localStore.get('profileActions', listed.key);
      if (!action) continue;
      if (cacheOwner() !== owner || !navigator.onLine) break;
      let patientId = action.request.patientMongoId;
      if (blocked.has(patientId)) continue;
      if (action.status === 'attention' && !(await recoverPrescriptionConflict(action, owner))) {
        blocked.add(patientId); continue;
      }
      if (action.nextAttemptAt > Date.now()) { blocked.add(patientId); continue; }
      try {
        assertViewerCanWrite();
        if (patientId.startsWith('local_')) {
          const mapping = await localStore.get('revisions', revisionKey(owner, patientId));
          if (!mapping?.serverPatientMongoId) { blocked.add(patientId); continue; }
          const serverId = mapping.serverPatientMongoId;
          action.request.patientMongoId = serverId;
          action.request.path = action.request.path.replace(patientId, serverId);
          if (action.request.body?.mongo_id === patientId) action.request.body.mongo_id = serverId;
          if (action.request.body?.patient_mongo_id === patientId) action.request.body.patient_mongo_id = serverId;
          if (String(action.request.body?.patient_id || '').startsWith('offline_') && mapping.patient?.patient_id) {
            action.request.body.patient_id = mapping.patient.patient_id;
          }
          action.request.baseRevision = mapping.revision;
          patientId = serverId;
          await localStore.put('profileActions', action);
        }
        if (action.attachments?.length && !action.frozen) {
          const urls = [];
          for (let index = 0; index < action.attachments.length; index++) {
            if (cacheOwner() !== owner) throw new Error('SESSION_CHANGED');
            const file = action.attachments[index];
            if (!file.url) {
              const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
              const target = ref(storage, `patient_reports/${action.request.doctorId}/${action.request.patientMongoId}/${action.operationId}_${index}_${safeName}`);
              await uploadBytes(target, file.blob, { contentType: file.blob.type || 'application/octet-stream' });
              file.url = await getDownloadURL(target);
              await localStore.put('profileActions', action);
            }
            urls.push(file.url);
          }
          if (action.attachmentMode === 'voice') {
            action.request.body.voice_notes[action.request.body.voice_notes.length - 1].audioUrl = urls[0];
          } else action.request.body.image_urls = urls;
        }
        // Freeze a revision before the first send. Retries must send identical bytes.
        if (!action.frozen) {
          action.frozen = true;
          await localStore.put('profileActions', action);
        }
        const token = await auth.currentUser.getIdToken();
        if (cacheOwner() !== owner) break;
        let response;
        const send = bearer => axios.post(`${API.BASE_URL}/api/offline/profile-actions`, action.request, {
          headers: { Authorization: `Bearer ${bearer}` }, timeout: 20000,
        });
        try { response = await send(token); }
        catch (error) {
          if (error.response?.status !== 401 || cacheOwner() !== owner) throw error;
          const refreshed = await auth.currentUser.getIdToken(true);
          if (cacheOwner() !== owner) throw new Error('SESSION_CHANGED');
          response = await send(refreshed);
        }
        if (response.data?.operationId !== action.operationId) throw new Error('Missing backend receipt');
        // Never rewrite a queued edit's base revision: replacement arrays (for
        // example voice notes) may otherwise erase another pending entry.
        await localStore.put('revisions', { key: revisionKey(owner, patientId), revision: response.data.revision });
        tracking(action, 'applied');
        await localStore.remove('profileActions', action.key);
        emit();
        if (cacheOwner() === owner) window.dispatchEvent(new CustomEvent('offlineClinicalApplied', { detail: { patientMongoId: patientId } }));
      } catch (error) {
        const status = error.response?.status;
        const terminal = status && status < 500 && ![408, 429].includes(status);
        const attempts = action.attempts + 1;
        action.status = terminal ? 'attention' : 'pending';
        action.error = error.response?.data?.code || error.code || 'CONNECTION_UNAVAILABLE';
        await localStore.put('profileActions', { ...action, attempts, nextAttemptAt: Date.now() + Math.min(300000, 2000 * 2 ** Math.min(attempts, 7)) });
        tracking(action, action.status, action.error);
        blocked.add(patientId); emit();
      }
    }
  } finally { running = false; }
}
export async function retryProfileAction(action) {
  if (action.owner !== cacheOwner()) return;
  await localStore.put('profileActions', { ...action, status: 'pending', nextAttemptAt: 0 });
  emit(); await synchronizeProfileActions();
}
export function profileWrite(method, url, body, config = {}) {
  const endpoint = new URL(url, API.BASE_URL);
  const owner = cacheOwner();
  const selectedAtSubmit = selected ? { ...selected } : null;
  const enabled = owner && canCaptureDoctor(localStorage.getItem('doctorId'));
  if (!enabled || !selected || endpoint.origin !== new URL(API.BASE_URL).origin || !allowed.some(([verb, pattern]) => method === verb && pattern.test(endpoint.pathname))) {
    return axios.request({ ...config, method, url, data: body });
  }
  if (navigator.onLine && !selectedAtSubmit._offlineDraft && !String(selectedAtSubmit._id || '').startsWith('local_')) {
    // Existing online calls stay unchanged. Only saves begun offline use the
    // new endpoint; an unknown outcome of a legacy call is never blindly replayed.
    return axios.request({ ...config, method, url, data: body });
  }
  const signature = JSON.stringify([owner, selectedAtSubmit._id, method, endpoint.pathname, body, config.params, config.offlineInventory]);
  if (submissions.has(signature)) return submissions.get(signature);
  const work = (async () => {
    assertViewerCanWrite();
    if (selectedAtSubmit.owner !== owner) throw new Error('Account changed. Reopen the patient profile.');
    const existing = (await pendingProfileActions()).find(action => action.signature === signature);
    if (!existing) {
      const operationId = crypto.randomUUID();
      const patient = selectedAtSubmit;
      const revision = await localStore.get('revisions', revisionKey(owner, patient._id));
      if (method !== 'POST' && !revision && !patient._offlineDraft) throw new Error('Download this profile online before editing it offline.');
      const action = {
        key: `${owner}:${operationId}`, operationId, actorUid: auth.currentUser.uid, owner, signature,
        patientName: patient.patient_name, createdAt: Date.now(), status: 'pending', attempts: 0, nextAttemptAt: 0,
        request: { operationId, schemaVersion: 1, patientMongoId: patient._id, doctorId: localStorage.getItem('doctorId'),
          method, path: endpoint.pathname,
          body: JSON.parse(JSON.stringify(endpoint.pathname === '/api/billing' ? { ...body, billingDate: body.billingDate || new Date().toISOString() } : body)),
          query: { ...Object.fromEntries(endpoint.searchParams), ...config.params },
          ...(config.offlineInventory?.length ? { inventory: config.offlineInventory } : {}),
          ...(revision ? { baseRevision: revision.revision } : {}) },
      };
      if (cacheOwner() !== owner) throw new Error('Account changed.');
      await localStore.put('profileActions', action);
      tracking(action, 'pending'); emit();
    }
    throw Object.assign(new Error('Saved on this device — pending synchronization. You can close this form; review the offline workspace for status.'), { code: 'OFFLINE_PROFILE_QUEUED' });
  })().finally(() => submissions.delete(signature));
  submissions.set(signature, work);
  return work;
}

export async function queueReportFiles(patient, files, payload, attachmentMode = 'reports') {
  assertViewerCanWrite();
  const owner = cacheOwner();
  if (!owner || !canCaptureDoctor(localStorage.getItem('doctorId')) || !patient?._id) throw new Error('Download and open an existing patient profile before saving reports offline.');
  if (!files.length || files.length > 10 || files.some(file => file.size > 10 * 1024 * 1024)) throw new Error('Choose up to 10 files, each no larger than 10 MB.');
  const hashes = [];
  for (const file of files) {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    hashes.push(Array.from(new Uint8Array(digest)).map(value => value.toString(16).padStart(2, '0')).join(''));
  }
  const signature = JSON.stringify([owner, patient._id, payload, hashes]);
  if ((await pendingProfileActions()).some(action => action.signature === signature)) return;
  const operationId = crypto.randomUUID();
  const revision = await localStore.get('revisions', revisionKey(owner, patient._id));
  if (attachmentMode === 'voice' && !revision) throw new Error('Download this patient profile before saving audio offline.');
  const action = {
    key: `${owner}:${operationId}`, operationId, actorUid: auth.currentUser.uid, owner, signature,
    patientName: patient.patient_name, createdAt: Date.now(), status: 'pending', attempts: 0, nextAttemptAt: 0,
    attachmentMode, attachments: files.map(file => ({ name: file.name, blob: file, url: '' })),
    request: { operationId, schemaVersion: 1, patientMongoId: patient._id, doctorId: localStorage.getItem('doctorId'),
      method: attachmentMode === 'voice' ? 'PUT' : 'POST',
      path: attachmentMode === 'voice' ? `/api/patients/${patient._id}` : '/api/patients/reports/upload-urls',
      body: { ...payload, mongo_id: patient._id }, query: {}, ...(revision ? { baseRevision: revision.revision } : {}) },
  };
  if (cacheOwner() !== owner) throw new Error('Account changed.');
  await localStore.put('profileActions', action);
  tracking(action, 'pending'); emit();
}
